import { Box, Button, CircularProgress, Typography } from '@mui/material';
import React, { useState } from 'react';
import DeleteModal from '../../../../sharedComponents/DeleteModal';
import { useTranslation } from 'react-i18next';
import Api, { ApiError } from '../../../../../data/api/Api';
import { useAppDispatch, useAppSelector } from '../../../../../reduxStore/redux-hooks';
import { setRefreshTitleFlag } from '../../../../../reduxStore/slices/historySlice';
import { SectionDivider } from '../../../../staticComponents/StaticHtmlGenerator';
import conversationHistoryPdf, { openFilePickerAndSaveFile } from '../conversationHistoryPdf';
import { HistoryConversationData } from '../History';
import { closeBasicPopoverManually, setClearChat } from '../../../../../reduxStore/slices/CommonSlice';
import { showSnackbarErrorMessage } from '../../../../../components/staticComponents/staticUtiles';
import { LogButtonEventToGA } from '../../../../../components/sharedComponents/googleAnalytics/GoogleAnalytics';

interface EditHistoryDataProps {
  conversationHeading: string;
  conversationID: string;
}

const EditHistoryData: React.FC<EditHistoryDataProps> = ({ conversationHeading, conversationID }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [openDatasetModal, setOpenDatasetModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isHistoryDownloading, setIsHistoryDownloading] = useState(false);
  const datasetUserPopupContent = `${t('rightPanel.confirmDelete')} "${conversationHeading}"?`;
  const datasetUserPopupButton = `${t('rightPanel.deleteButton')}`;
  const activeConversationID = useAppSelector((state) => state.history.historyState.conversationID);
  const handleClick = () => {
    LogButtonEventToGA(t('history.delete'));
    setOpenDatasetModal(true);
  };

  const closeDatasetModal = () => {
    LogButtonEventToGA(t('rightPanel.deleteButton'));
    setOpenDatasetModal(false);
  };

  const deleteDataset = async (_id: string) => {
    setIsDeleting(true);
    try {
      await Api.deleteHistory(_id);
    } catch (error) {
      console.error('Error deleting History:', error);
      showSnackbarErrorMessage(error as ApiError);
    } finally {
      setIsDeleting(false);
      closeDatasetModal();
      if (activeConversationID === _id) {
        dispatch(setClearChat(true));
      }
      dispatch(setRefreshTitleFlag());
    }
  };

  const handleHistoryDownload = async () => {
    LogButtonEventToGA(t('history.download'));
    setIsHistoryDownloading(true);
    try {
      const conversationMessages = (await Api.fetchMessages(conversationID)).data as HistoryConversationData[];
      const pdfBlob = await conversationHistoryPdf(conversationMessages);
      await openFilePickerAndSaveFile(pdfBlob);
      dispatch(closeBasicPopoverManually(true));
    } catch (error) {
      console.error('Error fetching Conversation Messages:', error);
      showSnackbarErrorMessage(error as ApiError);
    }
    setIsHistoryDownloading(false);
  };

  return (
    <Box sx={{ padding: '14px', width: '225px', borderRadius: '6px' }}>
      <Typography
        variant="h6"
        margin={'0'}
        color={'#000'}
        fontWeight={'700'}
        overflow={'hidden'}
        textOverflow={'ellipsis'}
        whiteSpace={'nowrap'}
      >
        {conversationHeading}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Typography
          sx={{ color: '#256AFF', textDecoration: 'underline', cursor: 'pointer' }}
          onClick={handleHistoryDownload}
          role="button"
          tabIndex={0}
        >
          {t('history.download')}
        </Typography>
        {isHistoryDownloading && <CircularProgress size={20} />}
      </Box>
      <SectionDivider sx={{ marginLeft: '0', marginRight: '0', marginBottom: '10px', height: '13px' }} />
      <Button sx={{ color: 'red', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => handleClick()}>
        {t('history.delete')}
      </Button>

      <DeleteModal
        openDeleteModal={openDatasetModal}
        closeDeleteModal={closeDatasetModal}
        modalAPi={() => {
          deleteDataset(conversationID);
        }}
        dialogTitle={t('rightPanel.deleteChat')}
        textContent={datasetUserPopupContent}
        confirmButtonText={datasetUserPopupButton}
      />

      {isDeleting && <CircularProgress size={20} />}
    </Box>
  );
};

export default EditHistoryData;
